import React, { useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../services/api";
import { sfx } from "../services/soundfx";

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      sfx.unlock();
      if (mode === "login") await login(email, password);
      else await register(email, password, name || "Parent");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(180deg,#38BDF8 0%,#A5F3FC 45%,#86EFAC 100%)" }}>
      {/* floating decor */}
      <div className="absolute top-10 left-8 text-6xl animate-floaty">☁️</div>
      <div className="absolute top-24 right-10 text-5xl animate-floaty" style={{ animationDelay: "1s" }}>⭐</div>
      <div className="absolute bottom-16 left-12 text-6xl animate-floaty" style={{ animationDelay: "0.5s" }}>🌈</div>

      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 18 }}
        className="relative w-full max-w-md rounded-[2.5rem] bg-white/95 border-[6px] border-amber-300 shadow-2xl p-7"
      >
        <div className="flex flex-col items-center mb-5">
          <div className="flex items-center justify-center w-20 h-20 rounded-3xl bg-amber-400 shadow-[0_6px_0_#D97706] mb-3">
            <BookOpen strokeWidth={2.5} className="w-11 h-11 text-white" />
          </div>
          <h1 className="font-display text-4xl font-black text-amber-500">Syllaberry</h1>
          <p className="font-bold text-slate-500 text-center text-sm mt-1">Parents sign in to start the reading adventure</p>
        </div>

        <div className="flex gap-2 mb-5 bg-slate-100 rounded-2xl p-1">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`tab-${m}`}
              onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 rounded-xl py-2 font-display font-bold transition-colors ${mode === m ? "bg-white shadow text-amber-600" : "text-slate-400"}`}
            >
              {m === "login" ? "Sign In" : "Sign Up"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          {mode === "register" && (
            <input
              data-testid="name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="rounded-2xl border-4 border-slate-200 focus:border-amber-300 outline-none px-4 py-3 font-bold text-slate-700"
            />
          )}
          <input
            data-testid="email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="rounded-2xl border-4 border-slate-200 focus:border-amber-300 outline-none px-4 py-3 font-bold text-slate-700"
          />
          <input
            data-testid="password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="rounded-2xl border-4 border-slate-200 focus:border-amber-300 outline-none px-4 py-3 font-bold text-slate-700"
          />

          {error && <p data-testid="auth-error" className="text-red-500 font-bold text-sm text-center">{error}</p>}

          <button
            data-testid="submit-btn"
            type="submit"
            disabled={busy}
            className="pop-btn mt-1 flex items-center justify-center gap-2 rounded-2xl bg-green-500 text-white font-display text-xl font-bold py-4 border-4 border-white shadow-[0_6px_0_#15803D] active:shadow-none disabled:opacity-60"
          >
            <Sparkles strokeWidth={2.5} /> {busy ? "Please wait..." : mode === "login" ? "Let's Learn!" : "Create Account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-0.5 bg-slate-200 rounded" />
          <span className="text-xs font-bold text-slate-400">or</span>
          <div className="flex-1 h-0.5 bg-slate-200 rounded" />
        </div>

        <button
          data-testid="google-signin-btn"
          type="button"
          onClick={() => {
            sfx.unlock();
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/profiles";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="pop-btn w-full flex items-center justify-center gap-3 rounded-2xl bg-white text-slate-700 font-display text-lg font-bold py-3.5 border-4 border-slate-200 hover:border-slate-300 shadow-[0_5px_0_#CBD5E1] active:shadow-none"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-6 h-6" />
          Continue with Google
        </button>

        <p className="text-center text-xs font-bold text-slate-400 mt-4">
          Demo: parent@phonicsquest.com / readwithme123
        </p>
      </motion.div>
    </div>
  );
}
